import React, { useEffect, useState, useMemo } from 'react';
import { Box, useTheme, Grid, CircularProgress } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { asyncFetchStories, asyncCreateStory, asyncDeleteStory, asyncUpdateStory, resetStories } from '../../../../store/Vanlifer/Community/storySlice';
import EditStoryDialog from '../Dialogs/EditStoryDialog';
import { Story, StoryImage, TourDetail } from '../../../../services/restserver-openapi';
import { useFetchStories } from "../useFetchStories";
import DistanceMarker from "./DistanceMarker";
import StoriesHeaderControls from './StoriesHeaderControl';
import StoryAccordion from "./StoryAccordion";
import ConfirmDialog from "../Dialogs/ConfirmDialog";

interface StoriesViewProps {
    tourId: number;
    chosenTourDetail: TourDetail;
    showSnackbar: (message: string, severity?: 'success' | 'error') => void;
    accessKey?: string;
}

export default function StoriesView({ tourId, chosenTourDetail, showSnackbar, accessKey }: StoriesViewProps) {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(state => state.login.isAuthenticated);
    const vanliferId = useAppSelector(state => state.user.vanliferId);
    const StoryState = useAppSelector(state => state.story);
    const tourDistances = useAppSelector(state => state.tour.detailedTourDistances);
    const userEntitlements = useAppSelector(state => state.user.entitlements);

    const { stories, loading, error, nextCursorId, nextCursorDate } = StoryState;

    const hostStays = useMemo(
        () => (chosenTourDetail.stays ?? []).filter((stay) => stay.stayType === 'HOST'),
        [chosenTourDetail.stays]
    );

    const isOwner = (isAuthenticated && chosenTourDetail.vanliferId === vanliferId) ?? false;

    const oldestFromDate = hostStays.length > 0
        ? hostStays.reduce((oldest, stay) => oldest.fromDate! < stay.fromDate! ? oldest : stay).fromDate
        : null;

    const newestToDate = hostStays.length > 0
        ? hostStays.reduce((newest, stay) => newest.toDate! > stay.toDate! ? newest : stay).toDate
        : null;

    const lastStay = hostStays.length > 0
        ? hostStays.reduce((latest, stay) => stay.fromDate! > latest.fromDate! ? stay : latest)
        : null;

    const tourHasStarted = oldestFromDate ? new Date(oldestFromDate) <= new Date() : false;
    const tourHasEnded = newestToDate ? new Date(newestToDate) < new Date() : false;
    const lastStayReached = lastStay ? new Date(lastStay.fromDate!) <= new Date() : false;

    const [editOpen, setEditOpen] = useState(false);
    const [selectedStory, setSelectedStory] = useState<Story | null>(null);
    const [storyToDelete, setStoryToDelete] = useState<Story | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [showFuture, setShowFuture] = useState(false);
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

    const canCreateContent = useMemo(() => {
        return userEntitlements.some(entitlement => entitlement.featureId === 'vanlifercreatecontent' && entitlement.value === "true")
    }, [userEntitlements]);

    const storiesLoaded = !loading || stories.length > 0;

    useEffect(() => {
        if (error) showSnackbar('Fehler beim Laden der Stories', 'error');
    }, [error, showSnackbar]);

    useEffect(() => {
        setShowFuture(!tourHasStarted || (tourHasStarted && tourHasEnded));
    }, [tourHasStarted, tourHasEnded]);

    useEffect(() => {
        if (!tourId) return;

        dispatch(resetStories());

        // original-condition beibehalten (ohne Kenntnis deiner auth/token Logik)
        const tokenInSession = sessionStorage.getItem("token");
        if (isAuthenticated || !tokenInSession) {
            dispatch(asyncFetchStories({
                tourId,
                limit: 10,
                sort: sortDirection,
                accessKey,
            }));
        }
    }, [dispatch, tourId, isAuthenticated, sortDirection, accessKey]);

    const { bottomRef, isFetchingMore } = useFetchStories({
        nextCursorId,
        nextCursorDate,
        loading,
        error,
        limit: 10,
        fetchAction: asyncFetchStories,
        fetchParams: {
            tourId,
            sort: sortDirection,
            accessKey,
        },
    });

    const [collapsedStays, setCollapsedStays] = useState<Set<number>>(new Set());

    const handleAccordionToggle = (stayId: number) => () => {
        setCollapsedStays(prev => {
            const newSet = new Set(prev);
            if (newSet.has(stayId)) newSet.delete(stayId);
            else newSet.add(stayId);
            return newSet;
        });
    };

    const handleCreateClick = async (stayId: number) => {
        const stay = hostStays.find(s => s.stayId === stayId);
        let toDate = stay?.toDate ? new Date(stay.toDate) : new Date();
        const now = new Date();

        if (now < toDate) toDate = now;
        else toDate.setHours(23, 59, 0, 0);

        const optimisticStory: Story = {
            storyId: -1,
            title: '',
            content: '',
            rating: 0,
            stayFk: stayId,
            sharingMode: Story.sharingMode.PRIVATE,
            storyDate: toDate.getTime(),
        };

        setSelectedStory(optimisticStory);
        setEditOpen(true);

        try {
            const created = await dispatch(asyncCreateStory({ tourId, stayId })).unwrap();
            setSelectedStory(prev => (prev?.storyId === -1 ? { ...prev, storyId: created.storyId } : prev));
        } catch {
            showSnackbar('Fehler beim Erstellen der Story', 'error');
            setEditOpen(false);
            setSelectedStory(null);
        }
    };

    const handleEditClick = (story: Story) => {
        setSelectedStory(story);
        setEditOpen(true);
    };

    const handleEditClose = () => setEditOpen(false);

    const [confirmDeleteTitle, setConfirmDeleteTitle] = useState<string | undefined>(undefined);

    const handleDeleteClick = (story: Story) => {
        setStoryToDelete(story);
        setConfirmDeleteTitle(story.title);
        setDeleteOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (storyToDelete?.storyId != null) {
            try {
                await dispatch(asyncDeleteStory(storyToDelete.storyId)).unwrap();
                showSnackbar('Story erfolgreich gelöscht', 'success');
            } catch {
                showSnackbar('Fehler beim Löschen der Story', 'error');
                dispatch(resetStories());
            }
        }
        setDeleteOpen(false);
        setStoryToDelete(null);
    };

    const handleEditSave = async (data: {
        title: string;
        description: string;
        rating: number;
        sharingMode: Story.sharingMode;
        storyDate: number;
        storyImages: StoryImage[];
    }) => {
        if (!selectedStory) return;

        const storyToSend = {
            storyId: selectedStory.storyId,
            title: data.title,
            content: data.description,
            rating: data.rating,
            tourFk: tourId,
            stayFk: selectedStory.stayFk,
            sharingMode: data.sharingMode,
            storyDate: data.storyDate,
            storyImages: data.storyImages?.map(({ storyImageId }) => ({ storyImageId })),
        };

        try {
            await dispatch(asyncUpdateStory(storyToSend as any)).unwrap();
            showSnackbar(selectedStory.rating === 0 ? 'Story erfolgreich erstellt' : 'Story erfolgreich aktualisiert', 'success');
        } catch {
            showSnackbar('Fehler beim Speichern der Story', 'error');
            dispatch(resetStories());
        }

        setEditOpen(false);
    };

    const theme = useTheme();
    const lineColor = theme.palette.divider;

    const isWithinCursorRange = (fromDate: number | undefined, toDate: number | undefined): boolean => {
        if (!fromDate) return false;
        if (!toDate) return false;
        if (!nextCursorDate) return true;
        return sortDirection === 'DESC'
            ? new Date(fromDate).setHours(0, 0, 0, 0) >= nextCursorDate
            : new Date(toDate).setHours(23, 59, 59, 999) <= nextCursorDate;
    };

    const sortedStays = hostStays
        ?.filter(s => {
            const from = s.fromDate ? new Date(s.fromDate) : null;
            const hasStarted = from && from <= new Date();
            const inCursorRange = isWithinCursorRange(s.fromDate, s.toDate);
            const stayStories = stories.filter(e => e.stayFk === s.stayId);
            return (inCursorRange || stayStories.length > 0) && (showFuture || hasStarted);
        })
        .sort((a, b) =>
            sortDirection === 'DESC'
                ? new Date(b.fromDate!).getTime() - new Date(a.fromDate!).getTime()
                : new Date(a.fromDate!).getTime() - new Date(b.fromDate!).getTime()
        ) || [];

    const skippedStays = !sortedStays ? 0 : hostStays.length - sortedStays.length;

    return (
        <>
            <Box sx={{ maxWidth: 800, mx: 'auto', pt: 0, pr: 2, pb: 2, pl: 2 }}>
                <StoriesHeaderControls
                    showFuture={showFuture}
                    setShowFuture={setShowFuture}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                    tourHasStarted={tourHasStarted}
                    lastStayReached={lastStayReached}
                />

                {!error && storiesLoaded && (
                    <DistanceMarker
                        text={
                            showFuture || lastStayReached
                                ? (sortDirection === 'ASC' ? 'Anreise' : 'Abreise')
                                : (sortDirection === 'ASC' ? 'Anreise' : 'Bald geht es weiter...')
                        }
                        color={theme.palette.text.secondary}
                    />
                )}

                {!error && storiesLoaded && (
                    sortedStays.map((stayDetail, index) => {
                        const stayStories = stories.filter(e => e.stayFk === stayDetail.stayId);

                        return (
                            <React.Fragment key={stayDetail.stayId}>
                                <StoryAccordion
                                    stay={stayDetail}
                                    stories={stayStories}
                                    isExpanded={!collapsedStays.has(stayDetail.stayId!)}
                                    onToggle={handleAccordionToggle(stayDetail.stayId!)}
                                    onCreateClick={handleCreateClick}
                                    onEdit={handleEditClick}
                                    onDelete={handleDeleteClick}
                                    isOwner={isOwner}
                                    canCreateContent={canCreateContent}
                                    sortDirection={sortDirection}
                                    lineColor={lineColor}
                                />

                                {index < sortedStays.length - 1 && (
                                    <DistanceMarker
                                        text={
                                            tourDistances &&
                                            tourDistances[sortDirection === 'DESC'
                                                ? tourDistances.length - 1 - index - (showFuture ? 0 : skippedStays)
                                                : index] !== undefined
                                                ? `${Math.round(
                                                    tourDistances[sortDirection === 'DESC'
                                                        ? tourDistances.length - 1 - index - (showFuture ? 0 : skippedStays)
                                                        : index]
                                                )} km`
                                                : "Entfernung unbekannt"
                                        }
                                        color={theme.palette.text.secondary}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })
                )}

                {!error && storiesLoaded && nextCursorId === null && nextCursorDate === null && (
                    <DistanceMarker
                        text={
                            showFuture || lastStayReached
                                ? (sortDirection === 'ASC' ? 'Abreise' : 'Anreise')
                                : (sortDirection === 'ASC' ? 'Bald geht es weiter...' : 'Anreise')
                        }
                        color={theme.palette.text.secondary}
                    />
                )}

                {(isFetchingMore || !storiesLoaded) && (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" mt={2}>
                            <CircularProgress size={32} />
                        </Box>
                    </Grid>
                )}

                <div ref={bottomRef} />
            </Box>

            <EditStoryDialog
                open={editOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
                dialogTitle={!selectedStory?.title?.trim()
                    ? `Neue Story bei ${hostStays.find(s => s.stayId === selectedStory?.stayFk)?.stayCompound?.name || 'unbekanntem Ort'}`
                    : `Story bei ${hostStays.find(s => s.stayId === selectedStory?.stayFk)?.stayCompound?.name || 'unbekanntem Ort'} bearbeiten`}
                initialTitle={selectedStory?.title || ''}
                initialDescription={selectedStory?.content || ''}
                storyId={selectedStory?.storyId || -1}
                initialImages={selectedStory?.storyImages || []}
                initialRating={selectedStory?.rating || 0}
                initialStoryDate={selectedStory?.storyDate || Date.now()}
                initialSharingMode={selectedStory?.sharingMode}
                stayFrom={new Date(
                    hostStays.find(s => s.stayId === selectedStory?.stayFk)?.fromDate || Date.now()
                ).getTime()}
                stayTo={new Date(
                    hostStays.find(s => s.stayId === selectedStory?.stayFk)?.toDate || Date.now()
                ).setHours(23, 59, 59, 999)}
            />

            <ConfirmDialog
                open={deleteOpen}
                title="Story löschen"
                content={`Die Story „${confirmDeleteTitle}“ wird dauerhaft gelöscht und kann nicht wiederhergestellt werden. Möchtest du wirklich fortfahren?`}
                onClose={() => {
                    setDeleteOpen(false);
                    setStoryToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                confirmText="Löschen"
                cancelText="Abbrechen"
                confirmColor="error"
                icon={<WarningIcon color="error" />}
            />
        </>
    );
}
